import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    getAuth,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { firebaseApp } from './firebaseConfig';
import { surrealClient } from './surrealClient';
import { useWingmanStore } from '../store/useWingmanStore';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const auth = getAuth(firebaseApp);
const googleProvider = new GoogleAuthProvider();

export const AuthContext = createContext();

// Helper function to fetch user tier from backend
async function fetchUserTierFromBackend(firebaseUid) {
    try {
        const gatewayUrl = import.meta.env.VITE_GATEWAY_URL;
        if (!gatewayUrl) return null;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
        
        const response = await authenticatedFetch(`${gatewayUrl}/payments/user-tier/${firebaseUid}`, {
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) return null;
        const data = await response.json();
        return data.tier || null;
    } catch (error) {
        console.warn('[Auth] Tier fetch bypassed (timeout or error)');
        return null;
    }
}

import { isDevCertModeActive, getDevCertUser } from './devCertAuthFixture';

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // Development-only certification auth seam. This must be checked before
            // Firebase so the explicit local fixture overrides an existing session.
            if (isDevCertModeActive()) {
                const certUser = getDevCertUser();
                if (certUser) {
                    if (typeof window !== 'undefined') {
                        window.__WINGMAN_USER_ID__ = certUser.uid;
                        window.__WINGMAN_FIREBASE_USER__ = certUser;
                        window.__AUTHENTICATED_FETCH__ = authenticatedFetch;
                    }
                    try {
                        const backendTier = await fetchUserTierFromBackend(certUser.uid);
                        useWingmanStore.getState().setUserTier(backendTier || 'developer');
                    } catch {
                        useWingmanStore.getState().setUserTier('developer');
                    }
                    setUser(certUser);
                    setLoading(false);
                    return;
                }
            }

            if (firebaseUser) {
                // Bridge for E2E Certification
                if (typeof window !== 'undefined') {
                    window.__WINGMAN_USER_ID__ = firebaseUser.uid;
                    window.__WINGMAN_FIREBASE_USER__ = firebaseUser;
                    window.__AUTHENTICATED_FETCH__ = authenticatedFetch;
                }
                try {
                    const backendTier = await fetchUserTierFromBackend(firebaseUser.uid);
                    if (backendTier) {
                        useWingmanStore.getState().setUserTier(backendTier);
                        console.log(`[Auth] Tier from backend: ${backendTier}`);
                    } else {
                        const profile = await surrealClient.getOrCreateProfile(firebaseUser.uid, firebaseUser.email || '');
                        useWingmanStore.getState().setUserTier(profile.tier);
                        console.log(`[Auth] Tier from SurrealDB: ${profile.tier}`);
                    }
                } catch (e) {
                    console.warn('[Auth] Tier sync failed, defaulting to free tier.', e);
                    useWingmanStore.getState().setUserTier('free');
                }
                setUser(firebaseUser);
            } else {
                setUser(null);
                useWingmanStore.getState().setUserTier('free');
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const signInEmail = async (email, password) => {
        setError(null);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (e) {
            setError(e.message);
            throw e;
        }
    };

    const signUpEmail = async (email, password) => {
        setError(null);
        try {
            await createUserWithEmailAndPassword(auth, email, password);
        } catch (e) {
            setError(e.message);
            throw e;
        }
    };

    const signInGoogle = async () => {
        setError(null);
        try {
            await signInWithPopup(auth, googleProvider);
        } catch (e) {
            setError(e.message);
            throw e;
        }
    };

    const resetPassword = async (email) => {
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (e) {
            setError(e.message);
            throw e;
        }
    };

    const signOut = async () => {
        await firebaseSignOut(auth);
        useWingmanStore.getState().setUserTier('free');
    };

    const authStatus = loading ? 'checking' : (user ? 'authenticated' : 'anonymous');

    const value = {
        user,
        loading,
        error,
        authStatus,
        signInEmail,
        signUpEmail,
        signInGoogle,
        resetPassword,
        signOut
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function useSafeAuth() {
    return useContext(AuthContext) || null;
}
